import { useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  ImportFindingKind,
  ImportSession,
  ImportShape,
  UnwindImportResponse,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { isApiError } from '../../api/errors';
import {
  useCommitImport,
  useCreditShortfall,
  useImportCreditPrice,
  usePreviewImport,
  useRetryImportRows,
  useUnwindImport,
} from '../../api/hooks';
import {
  IMPORT_CANCELED_LINE,
  IMPORT_COMMIT,
  IMPORT_COMMITTING,
  IMPORT_FINDINGS_UNCONFIRMED,
  IMPORT_KEEP,
  IMPORT_NOTES_FACTS_APPEAR,
  IMPORT_NOTES_FAILED_HEAD,
  IMPORT_NOTES_HEAD,
  IMPORT_OPEN_EVENT_ATTENDEES,
  IMPORT_PAUSED_LINE,
  IMPORT_PREVIEW_RUN,
  IMPORT_PRICE_CHANGED_LINE,
  maxPricedLabel,
  PLAN_EXCLUDES_PAID_WORK_LINE,
  IMPORT_PREVIEW_RUNNING,
  IMPORT_READING_DISCLOSURE,
  IMPORT_RESULT_HEAD,
  IMPORT_RETRY_ROWS_WORKING,
  IMPORT_SEE_THE_PEOPLE,
  IMPORT_UNDO,
  IMPORT_UNDOING,
  IMPORT_UNDO_LINE,
  IMPORT_UNDONE_HEAD,
  IMPORT_UNDO_KEPT_HEAD,
  IMPORT_UNDO_KEPT_WHY,
  importCommitBlockedBy,
  importPausedAtCeiling,
  importProgressLine,
  importReadNotesGist,
  importReadNotesLabel,
  importReadingDisclosure,
  importResultLines,
  importRetryLabel,
  importSummary,
  importUndoQuestion,
  importUndoRemovedLine,
  IMPORT_REPLACED_BY,
  IMPORT_REPLACED_BY_LINK,
} from '../../lib/copy';
import { formatCount } from '../../lib/format';
import { noteColumnsOf } from './plan-glance';
import { Checkbox } from '../../components/ui/checkbox';
import { CollapsedBin } from '../../components/ui/collapsed-bin';
import { ErrorLine } from '../../components/ui/error-line';
import { DangerLink } from '../../components/ui/button';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { WorkingButton } from '../../components/ui/working-button';
import { ReplaceDiffBlock } from './replace-diff';
import { FindingsBlock } from './findings';
import { useImportPage } from './session';
import { Section } from '../../components/ui/section';

/**
 * **The Import tab** (import note §1.9, §1.11, §2.4) — the one sentence saying
 * what this file is about to do, the two decisions that go with it, and the
 * button.
 *
 * After the commit it is the same tab reporting: what was written, what failed
 * and can be tried again, whether the reading paused at the monthly AI
 * allowance, and the
 * one way back out. Progress itself is the page's tinted line, in the run
 * strip's grammar — it is about the whole session rather than about this tab, so
 * it is visible from all four.
 *
 * **Which of the two halves is shown turns on `session.committed` and on
 * nothing else.** It used to turn on `state === 'in_progress' && phase !==
 * 'importing'`, and that reads as "has not committed" only until a commit stops
 * moving: a session that committed and finished its rows, and one that committed
 * and *failed*, both come back `phase: null` — the first at `state: 'complete'`,
 * the second still `in_progress`. So a failed commit put the whole door back on
 * screen, with the read-notes switch, the Import button and Cancel, on a session that had already written six hundred people. Pressing any
 * of them met a 409. `committed` is on the wire now for exactly this, and it is
 * `commit_decision_id IS NOT NULL`, which is what the server means by the word.
 * The one uncommitted session that gets no door is a canceled one.
 */
export function ImportCommitTab() {
  const { importId, session } = useImportPage();
  const org = useOrg();
  const summary = importSummary(
    session.counts,
    org.vertical,
    session.plan ? noteColumnsOf(session.plan) : [],
    session.target_event !== null,
  );

  return (
    <div className="import-commit">
      {/* **The summary is a list, not a sentence** (2026-09-06). It was one line
          of six numbers joined with middle dots and Lew could not read it; each
          fact is its own line now, under the head count they are all about. */}
      <FactLines head={summary.head} lines={summary.lines} />

      {/* A canceled session never committed, and its door would offer an
          Import the server refuses. */}
      {session.committed ? (
        <CommitResult importId={importId} session={session} />
      ) : session.state === 'in_progress' ? (
        <CommitDoor importId={importId} session={session} />
      ) : (
        <p className="src">{IMPORT_CANCELED_LINE}</p>
      )}
    </div>
  );
}

/**
 * **A stack of counts, one fact per line** (2026-09-06).
 *
 * Both halves of this tab said several numbers about one thing, and both said
 * them as a single line joined with middle dots — "226 people: 1 members, 225
 * contacts · 1 already on record · 225 notes to read" before the commit, six
 * more clauses after it. Lew could not read either: there is no way to find the
 * one number you came for without reading all of them. They are lines now, on
 * the 6px rhythm `.fact-lines` carries.
 *
 * `head` is the count the rest are about — the summary has one (how many people
 * the file produces), the result does not, because its first line is already a
 * fact rather than a total. The lines are their own keys: each is a distinct
 * sentence about a distinct count, so two identical ones would be a bug in the
 * builder rather than a list this component has to disambiguate.
 */
export function FactLines({ head, lines }: { head?: string; lines: string[] }) {
  return (
    <ul className="fact-lines">
      {head !== undefined ? <li className="head">{head}</li> : null}
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/* ————————————————————————— before the commit ————————————————————————— */

function CommitDoor({ importId, session }: { importId: string; session: ImportSession }) {
  const commit = useCommitImport(importId);
  const preview = usePreviewImport(importId);
  /* **An enrolled account's Import says the most it can cost** (credits launch
     spec, step 4): the rows beyond the month's included rows, held at the press
     and charged only for the rows written. No confirmation, as on every priced
     button; the price turns on the dry run, so it is read again when that moves. */
  const price = useImportCreditPrice(importId, session.previewed_at);
  const short = useCreditShortfall(price.units);

  const [readNotes, setReadNotes] = useState(true);

  /* **The kinds the office has ticked**, and only the kinds: a box is about a
     finding's *kind* because that is what the server confirms against, and a
     sentence echoed back would be a sentence one character could get wrong.
     Held per press rather than stored — the confirmation is a decision about
     this commit (§4.3), which is the same thing `read_notes` is. */
  const [confirmed, setConfirmed] = useState<ImportFindingKind[]>([]);
  const findings = session.findings;

  /**
   * **A tick survives a re-render and not a new list.**
   *
   * This page polls, and the office may run the dry run again from the bar above
   * it while the boxes are ticked. If the list that comes back is a different
   * list, every tick on it is a confirmation of something nobody was shown —
   * which is the one thing the whole gate exists to prevent, arriving by the
   * back door.
   *
   * **Keyed on the sentence and not the kind**, because the sentence is what is
   * being confirmed: `names_repeat` going from "12 of 12 rows" to "143 of 176
   * rows" is the same kind and a different statement, and the box has to come
   * back empty for it. Same list, same signature, ticks kept — which is what
   * makes a poll that changes nothing leave the office's work alone.
   *
   * The reset happens *during* render rather than in an effect, so the first
   * paint of a new list already has the button off; an effect would paint one
   * frame of a live Import button over findings nobody has read, and would not
   * run at all under `renderToStaticMarkup`.
   */
  const signature = findings.map((finding) => `${finding.kind} ${finding.line}`).join('\n');
  const [confirmedFor, setConfirmedFor] = useState(signature);
  if (confirmedFor !== signature) {
    setConfirmedFor(signature);
    setConfirmed([]);
  }
  const ticked = confirmedFor === signature ? confirmed : [];

  /* **A replace's promoted drops**, by the same rule as the ticks above: a new
     diff clears them, so nothing is taken off that the office was not shown. */
  const diff = session.replaces_list ? session.replace_diff : null;
  const diffSignature = diff ? diff.on_record.map((one) => one.node_id).join(',') : '';
  const [drops, setDrops] = useState<string[]>([]);
  const [dropsFor, setDropsFor] = useState(diffSignature);
  if (dropsFor !== diffSignature) {
    setDropsFor(diffSignature);
    setDrops([]);
  }
  const unconfirmed = findings.filter((finding) => !ticked.includes(finding.kind));

  const blocked = importCommitBlockedBy(session);
  /* Two of the three reasons in `blocked` are answered by Run preview, which is
     in the bar above the tabs and nowhere else (import UX audit, 2026-10-24);
     the third (rows that still need a look) is a list, and the People tab is
     where it is answered.

     **The fourth reason, and the only one whose gesture is on this tab.** It is
     last because the other three are more specific: a session with no dry run
     has no findings to have found, and rows still in the bin are a thing to
     answer before a sentence about the whole file means anything.
     `importCommitBlockedBy` is untouched — it answers from the session alone and
     this is about a box somebody has or has not ticked. */
  const stopped =
    blocked ??
    (unconfirmed.length > 0 ? IMPORT_FINDINGS_UNCONFIRMED : null) ??
    (price.units === null ? PLAN_EXCLUDES_PAID_WORK_LINE : null);

  return (
    <>
      {/* **The decision is one block, and the button closes it** (§1.9,
          §1.11). One stack on the page's 12px rhythm: what happens to the
          notes, then Import. */}
      <div className="commit-door">
        {session.counts.notes_to_read > 0 ? (
          <NotesDecision
            shape={session.shape}
            checked={readNotes}
            disabled={commit.isPending}
            onChange={setReadNotes}
          />
        ) : null}

        {/* **The reason is tied to the control it is about.** A disabled button
            announces itself as "Import, dimmed" and nothing else; the sentence
            saying why sat as a loose line somewhere below it. `aria-describedby`
            on a wrapper is what joins the two — the button itself cannot carry
            it, because a `WorkingButton` is a component and the attribute has to
            reach the element. */}
        {diff ? (
          <ReplaceDiffBlock
            diff={diff}
            dropped={drops}
            disabled={commit.isPending}
            onToggle={(nodeId, checked) =>
              setDrops((held) =>
                checked ? [...new Set([...held, nodeId])] : held.filter((one) => one !== nodeId),
              )
            }
          />
        ) : null}

        {findings.length > 0 ? (
          <FindingsBlock
            findings={findings}
            confirmed={ticked}
            disabled={commit.isPending}
            onToggle={(kind, checked) =>
              setConfirmed((held) =>
                checked ? [...new Set([...held, kind])] : held.filter((one) => one !== kind),
              )
            }
          />
        ) : null}

        <div
          className="layer-actions"
          aria-describedby={
            findings.length > 0
              ? stopped
                ? 'import-findings import-blocked'
                : 'import-findings'
              : stopped
                ? 'import-blocked'
                : undefined
          }
        >
          <WorkingButton
            label={maxPricedLabel(IMPORT_COMMIT, price.units, price.unitsPerCredit)}
            workingLabel={IMPORT_COMMITTING}
            working={commit.isPending}
            disabled={stopped !== null || price.pending || short !== null}
            onClick={() =>
              commit.mutate({
                read_notes: readNotes,
                /* **Each ticked finding as the page drew it** — its kind and
                   the sentence the office actually read. The server compares
                   both, so a dry run that landed between the tick and the press
                   is refused rather than waved through on a matching category;
                   and nothing the page is no longer showing is sent at all, a
                   stale tick on a dropped finding being a confirmation of
                   nothing. */
                confirm_findings: findings
                  .filter((finding) => ticked.includes(finding.kind))
                  .map((finding) => ({ kind: finding.kind, line: finding.line })),
                ...(typeof price.units === 'number' ? { expected_credit_units: price.units } : {}),
                ...(diff && drops.length ? { drop_from_event: drops } : {}),
              })
            }
          />
          {short ? <span className="credit-short">{short}</span> : null}
        </div>
      </div>

      {/* The reason is on the page rather than behind the press: the server has
          two refusals here and both are things the office can see and fix. */}
      {stopped ? (
        <div className="src" id="import-blocked">
          <div>{stopped}</div>
          {preview.isError ? <ErrorLine error={preview.error} /> : null}
        </div>
      ) : null}
      {commit.isError ? (
        isApiError(commit.error) && commit.error.code === 'CREDIT_PRICE_CHANGED' ? (
          <div className="src">{IMPORT_PRICE_CHANGED_LINE}</div>
        ) : isApiError(commit.error) && commit.error.code === 'IMPORT_REPLACE_STALE' ? (
          /* The list moved since the dry run: the refusal carries the gesture. */
          <div className="src">
            <ErrorLine error={commit.error} />
            <div className="layer-actions">
              <WorkingButton
                label={IMPORT_PREVIEW_RUN}
                workingLabel={IMPORT_PREVIEW_RUNNING}
                variant="act"
                working={preview.isPending}
                onClick={() => {
                  commit.reset();
                  preview.mutate();
                }}
              />
            </div>
          </div>
        ) : (
          <ErrorLine error={commit.error} />
        )
      ) : null}
    </>
  );
}

/* ————————————————————————— the notes decision ————————————————————————— */

/**
 * **The switch, what it buys, and what reading actually is** (§1.9, §1.11).
 *
 * The complaint was that the block could not be deciphered: a checkbox saying
 * only its own name, a bare `about $3` stamped after it, and a sentence about
 * the *order* of events above it — three fragments, none of which answered
 * "what happens if I tick this". So the line beside the switch is now the
 * answer in one sentence, with what bounds the cost a shade fainter after it,
 * and the mechanism — what a pass is, what it costs, and what leaving it off
 * costs you — waits behind a disclosure for the reader who wants it.
 *
 * **No figure anywhere in this block** (2026-09-06). The `about $3` came off the
 * session's estimate, and the estimate was an order of magnitude under what a
 * bio costs to read; it is withdrawn rather than corrected, because the cost
 * varies with how much each unit says and no single number is honest. The
 * spending limit under this switch is what the office is given instead — a
 * bound it sets, rather than a forecast it is handed.
 *
 * The words follow the file: a **document**'s units are bios, a **grid**'s are
 * notes, the same split the People tab's rows make.
 *
 * Drawn only when `counts.notes_to_read > 0`. A ticked switch above "This file
 * has no notes to read" asked a question with no answer, so a file with nothing
 * to read shows no switch at all (import UX audit, 2026-10-24).
 */
export function NotesDecision({
  shape,
  checked,
  disabled,
  onChange,
}: {
  shape: ImportShape;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="field">
      <div className="label">{IMPORT_NOTES_HEAD}</div>
      <Checkbox
        className="near-match notes-switch"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      >
        {importReadNotesLabel(shape)}
        <span className="switch-gist">{importReadNotesGist(shape)}</span>
      </Checkbox>
      {/* Closed by default and in the system's own disclosure grammar — the
          same caret the collapsed bins wear, so "there is more behind this" is
          one idea across the app rather than a new one on this tab. */}
      <CollapsedBin label={IMPORT_READING_DISCLOSURE}>
        {importReadingDisclosure(shape).map((paragraph) => (
          <p className="bin-para" key={paragraph.slice(0, 24)}>
            {paragraph}
          </p>
        ))}
      </CollapsedBin>
    </div>
  );
}

/* ————————————————————————— after it ————————————————————————— */

/**
 * **What a committed session offers**, and nothing that would be refused.
 *
 * Three things, each on its own condition: **Retry** when rows failed or notes
 * did; **Undo** whenever this session wrote something and has not already been
 * taken back; and the way to the people themselves. A reading paused at the
 * monthly AI allowance is said here in one line and answered in the banner
 * under the head, which holds **Read the rest** for all four tabs.
 *
 * Undo is gated on `committed && !undone`, which is what the server's own two
 * refusals say (`IMPORT_NOT_COMMITTED`, `IMPORT_ALREADY_UNDONE`). It used to be
 * gated on `state === 'complete'`, which is a narrower thing: a commit that
 * failed part way through stays `in_progress`, and that is precisely the session
 * somebody wants to undo. The count in the question is `created + attached` —
 * the people this file actually put on record — rather than every row it read.
 */
function CommitResult({ importId, session }: { importId: string; session: ImportSession }) {
  const retry = useRetryImportRows(importId);
  const unwind = useUnwindImport(importId);

  const [confirmingUndo, setConfirmingUndo] = useState(false);

  const counts = session.counts;
  const wrote = counts.by_outcome.created + counts.by_outcome.attached;
  const paused = importPausedAtCeiling(session);
  const undone = session.state === 'undone';
  /* Kept on the session since 2026-09-06, so it survives a reload — this is the
     one moment somebody comes back to re-read the list of people that were
     kept, and a report held in this component's state was gone by then. */
  const report = session.unwind_report;
  const retryable = counts.by_outcome.failed + counts.notes_failed > 0;

  return (
    <>
      {/* **An undone session leads with the undo** (import UX audit,
          2026-10-24): what came off the records, and who was kept, is what
          somebody comes back to read; what the import had written sits under it. */}
      {report?.replaced_by ? (
        <div className="src">
          {IMPORT_REPLACED_BY}{' '}
          <Link className="link" to={`/imports/${report.replaced_by}`}>
            {IMPORT_REPLACED_BY_LINK}
          </Link>
        </div>
      ) : null}
      {report ? <UnwindReport report={report} /> : null}

      <Section title={IMPORT_RESULT_HEAD}>
        {/* **One fact per line** (2026-09-06), for the reason the summary above
            it is a list: the six clauses were unreadable strung across a line.
            The sentences under it — the progress fraction and what it means —
            stay sentences, because they are prose rather than counts. */}
        <FactLines lines={importResultLines(session)} />

        {/* **While the reading is running, on the tab that started it.** The
            fraction is the strip's sentence, repeated here because this is where
            somebody is standing after pressing Import — and under it the one
            thing the fraction does not say: the records are filling in as it
            goes, so a person read three minutes ago has more on them than a
            person read three seconds ago, and neither is finished. */}
        {session.phase === 'reading_notes' ? (
          <div className="src" aria-live="polite">
            <div>{importProgressLine(session)}</div>
            <div>{IMPORT_NOTES_FACTS_APPEAR}</div>
          </div>
        ) : null}

        {counts.links_skipped > 0 ? (
          <div className="src">
            {formatCount(counts.links_skipped)} of them are already recorded as someone who signs in
            here, so that link was left alone.
          </div>
        ) : null}

        {counts.notes_failed > 0 ? (
          <div className="src">
            {IMPORT_NOTES_FAILED_HEAD}: {formatCount(counts.notes_failed)}. They are still on the
            people, unread.
          </div>
        ) : null}
      </Section>

      {/* **The status, and not the decision** (2026-09-06). The banner under
          the head carries **Read the rest**, on every tab, and two copies of one
          control is two things to keep in step. What is left is the fact,
          because somebody reading the result of an import wants to know its
          reading stopped without looking up. */}
      {paused ? (
        <div className="section">
          <div className="tag-faint amber-ink block">{IMPORT_PAUSED_LINE}</div>
        </div>
      ) : null}

      {/* One footer row: the way to the people, Retry, and Undo last as the
          page's destructive action, set off by its hairline. */}
      {retryable || !undone ? (
        <div className="layer-actions">
          {wrote > 0 && !undone ? (
            session.target_event ? (
              /* An attendee list went onto the event, and that list is where
                 its people are; most of them have no People-list record. */
              <Link className="btn go" to={`/events/${session.target_event.entity_id}/attendees`}>
                {IMPORT_OPEN_EVENT_ATTENDEES}
              </Link>
            ) : (
              /* `level=all` on purpose: an import writes known people as well as
                 tracked ones, and the list defaults to tracked — a link that
                 showed half of what it just wrote would read as a failure. */
              <Link className="btn go" to={`/people?import=${importId}&level=all`}>
                {IMPORT_SEE_THE_PEOPLE}
              </Link>
            )
          ) : null}
          {retryable ? (
            <WorkingButton
              label={importRetryLabel(counts.by_outcome.failed, counts.notes_failed)}
              workingLabel={IMPORT_RETRY_ROWS_WORKING}
              variant="act"
              working={retry.isPending}
              onClick={() => retry.mutate()}
            />
          ) : null}
          {!undone ? (
            <DangerLink onClick={() => setConfirmingUndo(true)}>{IMPORT_UNDO}</DangerLink>
          ) : null}
        </div>
      ) : null}
      {retry.isError ? <ErrorLine error={retry.error} /> : null}

      {confirmingUndo ? (
        <ConfirmModal
          title={importUndoQuestion(wrote, session.target_event !== null)}
          line={IMPORT_UNDO_LINE}
          confirmLabel={IMPORT_UNDO}
          workingLabel={IMPORT_UNDOING}
          working={unwind.isPending}
          danger
          onConfirm={() => unwind.mutate(undefined, { onSuccess: () => setConfirmingUndo(false) })}
          dismissLabel={IMPORT_KEEP}
          onClose={() => setConfirmingUndo(false)}
        >
          {unwind.isError ? <ErrorLine error={unwind.error} /> : null}
        </ConfirmModal>
      ) : null}
    </>
  );
}

/**
 * **What the undo took back, and who it stopped at** (§4.1's *stop and report*).
 *
 * The kept list is the whole point of the answer: those people are still on the
 * records, and the office is being told which ones and why rather than left to
 * discover it. Each is a link, because the way to remove one is on their own
 * record.
 *
 * `aria-live="polite"` because the section appears where a modal used to be, and
 * the modal's own closing hands focus back to the button that opened it — so a
 * reader using speech is standing on "Undo this import" with a report they were
 * never told arrived. Polite rather than assertive: nothing here is urgent, and
 * the undo has already happened.
 */
export function UnwindReport({ report }: { report: UnwindImportResponse }) {
  return (
    <div className="section" aria-live="polite">
      <h2>{IMPORT_UNDONE_HEAD}</h2>
      <p className="lede">{importUndoRemovedLine(report.removed)}</p>

      {report.kept.length > 0 ? (
        <>
          <div className="src">{IMPORT_UNDO_KEPT_HEAD}</div>
          <div className="src">{IMPORT_UNDO_KEPT_WHY}</div>
          {report.kept.map((person) => (
            <div className="act-row" key={person.entity_id}>
              <div className="act-name">
                <Link className="link" to={`/people/${person.entity_id}`}>
                  {person.name}
                </Link>
              </div>
              <div className="act-value fill">{person.reason}</div>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}
